import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { loadMoyasarFormAssets } from './loadMoyasarForm';
import { getMoyasarPublishableKey, isMoyasarTestMode } from './moyasarEnv';
import { formatAmountHalalas, loadCheckoutSession } from './checkoutSession';
import './PaymentCheckoutPage.css';

const PaymentCheckoutPage = () => {
  const { paymentId: paymentIdParam } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const formContainerRef = useRef(null);
  const formInitializedRef = useRef(false);

  const [checkout, setCheckout] = useState(null);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isLoadingForm, setIsLoadingForm] = useState(true);

  useEffect(() => {
    const paymentId = String(paymentIdParam || '').trim();
    if (!paymentId) {
      setError(t('payment.missingPaymentId', { defaultValue: 'Payment reference is missing.' }));
      setIsLoadingForm(false);
      return;
    }

    const fromState = location.state?.checkout;
    const session = loadCheckoutSession(paymentId);
    const resolved = fromState || session;

    if (!resolved || String(resolved.paymentId) !== paymentId) {
      setError(
        t('payment.sessionExpired', {
          defaultValue: 'Checkout session expired. Please start again from the pricing page.',
        })
      );
      setIsLoadingForm(false);
      return;
    }

    if (!resolved.moyasar?.amount || !resolved.moyasar?.currency || !resolved.moyasar?.callbackUrl) {
      setError(
        t('payment.invalidCheckout', {
          defaultValue: 'Checkout details are incomplete. Please try again from pricing.',
        })
      );
      setIsLoadingForm(false);
      return;
    }

    setCheckout(resolved);
  }, [location.state, paymentIdParam, t]);

  useEffect(() => {
    if (!checkout || formInitializedRef.current) {
      return undefined;
    }

    const publishableKey = getMoyasarPublishableKey();
    if (!publishableKey) {
      setFormError(
        t('payment.publishableKeyMissing', {
          defaultValue: 'Payment form is not configured (publishable key missing).',
        })
      );
      setIsLoadingForm(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const Moyasar = await loadMoyasarFormAssets();
        if (cancelled || !formContainerRef.current) return;

        const amount = Math.trunc(Number(checkout.moyasar.amount));
        if (!Number.isSafeInteger(amount) || amount <= 0) {
          throw new Error('Invalid checkout amount');
        }

        const metadata = {
          ydim_invoice_id: String(checkout.invoiceId),
          ydim_payment_id: String(checkout.paymentId),
        };
        if (user?.id) {
          metadata.ydim_user_id = String(user.id);
        }

        Moyasar.init({
          element: formContainerRef.current,
          amount,
          currency: checkout.moyasar.currency,
          description: `${checkout.planId} ${checkout.billingInterval} subscription`,
          publishable_api_key: publishableKey,
          callback_url: checkout.moyasar.callbackUrl,
          methods: ['creditcard'],
          supported_networks: ['mada', 'visa', 'mastercard', 'amex', 'unionpay'],
          metadata,
          on_completed: async function onMoyasarCompleted() {
            // Phase 3: Moyasar handles redirect; webhook updates invoice/payment server-side.
          },
          on_failure: async function onMoyasarFailure(failureMessage) {
            if (!cancelled) {
              setFormError(
                typeof failureMessage === 'string'
                  ? failureMessage
                  : t('payment.formFailed', { defaultValue: 'Payment could not be completed.' })
              );
            }
          },
        });

        formInitializedRef.current = true;
        setIsLoadingForm(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Moyasar form init error:', err);
          setFormError(
            t('payment.formLoadFailed', {
              defaultValue: 'Failed to load the secure payment form. Please try again.',
            })
          );
          setIsLoadingForm(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkout, t, user?.id]);

  const publishableKey = getMoyasarPublishableKey();
  const testMode = isMoyasarTestMode(publishableKey);
  const invoiceLabel = checkout?.invoiceId
    ? `INV-${checkout.invoiceId}`
    : '—';

  return (
    <div className="payment-checkout-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="payment-checkout-card">
        <header className="payment-checkout-header">
          <h1>{t('payment.title', { defaultValue: 'Complete Payment' })}</h1>
          {testMode && (
            <span className="payment-checkout-test-badge">
              {t('payment.testMode', { defaultValue: 'Test mode' })}
            </span>
          )}
        </header>

        {error && (
          <div className="payment-checkout-error">
            <p>{error}</p>
            <Link to="/pricing" className="payment-checkout-back-link">
              {t('payment.backToPricing', { defaultValue: 'Back to pricing' })}
            </Link>
          </div>
        )}

        {!error && checkout && (
          <>
            <dl className="payment-checkout-summary">
              <div>
                <dt>{t('payment.invoiceNumber', { defaultValue: 'Invoice' })}</dt>
                <dd>{invoiceLabel}</dd>
              </div>
              <div>
                <dt>{t('payment.amount', { defaultValue: 'Amount' })}</dt>
                <dd>{formatAmountHalalas(checkout.moyasar.amount, checkout.moyasar.currency)}</dd>
              </div>
              <div>
                <dt>{t('payment.plan', { defaultValue: 'Plan' })}</dt>
                <dd>
                  {checkout.planId}
                  {checkout.billingInterval ? ` (${checkout.billingInterval})` : ''}
                </dd>
              </div>
            </dl>

            {formError && (
              <div className="payment-checkout-error">
                <p>{formError}</p>
              </div>
            )}

            {isLoadingForm && !formError && (
              <p className="payment-checkout-loading">
                {t('payment.loadingForm', { defaultValue: 'Loading secure payment form...' })}
              </p>
            )}

            <div
              ref={formContainerRef}
              className="payment-checkout-moyasar-form"
              aria-live="polite"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCheckoutPage;
